import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check, Play, Users, Building, DollarSign, BarChart3, CheckCircle, Zap } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const TutorialModal = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: "Welcome to Bissa!",
      subtitle: "Let's build meaningful relationships together",
      content: (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-[#0176D3] to-[#0155A8] rounded-full flex items-center justify-center mx-auto">
            <Play className="w-10 h-10 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to build connections like the Bissa?</h3>
            <p className="text-gray-600">
              This tutorial will show you how Bissa.ai empowers your business with relationship-building 
              tools that honor tradition while embracing AI innovation. Learn to strengthen every connection.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-[#0176D3] font-medium">
              ⏱️ Time required: 5 minutes
            </p>
          </div>
        </div>
      ),
      action: "Let's Start!"
    },
    {
      title: "Step 1: Building Relationships",
      subtitle: "Your connection database is the heart of your business",
      content: (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[#0176D3]" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Create Meaningful Connections</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Click "Contacts" to access your relationship database</li>
                <li>• Add contacts with rich context: background, preferences, cultural notes</li>
                <li>• Search and organize by relationship strength, interaction history</li>
                <li>• Import your network from CSV files via Import/Export</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-[#0176D3]" />
              <span className="font-medium text-[#0176D3]">Bissa Wisdom</span>
            </div>
            <p className="text-sm text-gray-700">
              Like the Bissa traders, tag contacts by relationship type (community, partners, prospects) to honor the different connections in your network.
            </p>
          </div>
        </div>
      ),
      action: "Next: Communities"
    },
    {
      title: "Step 2: Community Building",
      subtitle: "Organize relationships within business communities",
      content: (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Build Business Communities</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Navigate to "Companies" to organize business relationships</li>
                <li>• Connect individuals to their business communities</li>
                <li>• Track community influence, cultural context, and collaboration patterns</li>
                <li>• See the full network of connections within each organization</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-600">Bissa Way</span>
            </div>
            <p className="text-sm text-gray-700">
              Map business relationships like traditional trade networks - understanding community structure strengthens every connection.
            </p>
          </div>
        </div>
      ),
      action: "Next: Trust-Building"
    },
    {
      title: "Step 3: Sales Pipeline",
      subtitle: "Track deals from lead to close",
      content: (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Manage Your Sales Pipeline</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Go to "Deals" to see your visual sales pipeline</li>
                <li>• Drag and drop deals between stages (Lead, Qualified, Proposal, Closed)</li>
                <li>• Set deal values and expected close dates</li>
                <li>• Link deals to contacts and companies</li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div className="bg-yellow-100 p-2 rounded text-center">
              <div className="text-xs font-medium text-yellow-700">Lead</div>
            </div>
            <div className="bg-blue-100 p-2 rounded text-center">
              <div className="text-xs font-medium text-blue-700">Qualified</div>
            </div>
            <div className="bg-purple-100 p-2 rounded text-center">
              <div className="text-xs font-medium text-purple-700">Proposal</div>
            </div>
            <div className="bg-green-100 p-2 rounded text-center">
              <div className="text-xs font-medium text-green-700">Closed</div>
            </div>
          </div>
        </div>
      ),
      action: "Next: Analytics"
    },
    {
      title: "Step 4: Analytics & Reports",
      subtitle: "Track your performance and growth",
      content: (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Monitor Your Success</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Check your "Dashboard" for key metrics and recent activity</li>
                <li>• Visit "Reports" for detailed analytics and trends</li>
                <li>• Track conversion rates, deal velocity, and revenue</li>
                <li>• Monitor team performance and pipeline health</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-600">Key Metrics</span>
            </div>
            <p className="text-sm text-gray-700">
              Focus on conversion rates and deal velocity to identify bottlenecks and optimize your sales process.
            </p>
          </div>
        </div>
      ),
      action: "Complete Tutorial"
    },
    {
      title: "🎉 Ready to Build Your Network!",
      subtitle: "Carry forward the Bissa legacy of meaningful connection",
      content: (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">You're Connected!</h3>
            <p className="text-gray-600 mb-6">
              You've learned how Bissa.ai helps you build meaningful relationships through technology that honors 
              tradition. Now you can connect authentically while growing intelligently.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-[#0176D3] to-[#0155A8] text-white p-4 rounded-lg">
              <h4 className="font-semibold mb-2">The Bissa Way Forward:</h4>
              <ul className="text-sm space-y-1 text-left">
                <li>✓ Import and organize your existing relationships</li>
                <li>✓ Create your first trust-based opportunities</li>
                <li>✓ Set up cultural context and relationship stages</li>
                <li>✓ Explore AI insights for deeper connections</li>
              </ul>
            </div>
          </div>
        </div>
      ),
      action: "Start Building Relationships"
    }
  ];

  const currentStepData = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentStepData.title}</h2>
            <p className="text-gray-600 mt-1">{currentStepData.subtitle}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-8 py-4 border-b border-gray-100">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Step {currentStep + 1} of {tutorialSteps.length}</span>
            <span>{Math.round(((currentStep + 1) / tutorialSteps.length) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-[#0176D3] to-[#0155A8] h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-8">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-6 flex justify-between items-center rounded-b-2xl">
          <button
            onClick={handlePrevious}
            disabled={isFirstStep}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isFirstStep 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex gap-2">
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-[#0176D3]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2 bg-[#0176D3] text-white rounded-lg font-medium hover:bg-[#0155A8] transition-colors"
          >
            {currentStepData.action}
            {!isLastStep && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;